import React, { useState } from 'react';
import { Order } from '../../../types/order';

interface ScheduleDeliveriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deliveryDate: Date) => void;
  selectedOrderIds: string[];
  orders: Order[];
}

const ScheduleDeliveriesModal: React.FC<ScheduleDeliveriesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedOrderIds,
  orders
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selectedDate);
    onClose();
  };

  // Filter orders to only show selected deliveries
  const selectedDeliveries = orders.filter(order => selectedOrderIds.includes(order.id));

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isToday = (date: Date) => {
    return isSameDay(date, new Date());
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const selectDate = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (!isPastDate(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={styles.calendarDayEmpty}></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isSelected = isSameDay(date, selectedDate);
      const isTodayDate = isToday(date);
      const isPast = isPastDate(date);

      days.push(
        <button
          key={day}
          type="button"
          style={{
            ...styles.calendarDay,
            ...(isSelected && styles.calendarDaySelected),
            ...(isTodayDate && !isSelected && styles.calendarDayToday),
            ...(isPast && styles.calendarDayPast)
          }}
          onClick={() => selectDate(day)}
          disabled={isPast}
        >
          {day}
        </button>
      );
    }

    return (
      <div style={styles.calendarContainer}>
        <div style={styles.calendarHeader}>
          <button type="button" style={styles.calendarNavButton} onClick={previousMonth}>
            ‹
          </button>
          <div style={styles.calendarMonthYear}>{monthName}</div>
          <button type="button" style={styles.calendarNavButton} onClick={nextMonth}>
            ›
          </button>
        </div>
        <div style={styles.calendarWeekdays}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={styles.calendarWeekday}>
              {day}
            </div>
          ))}
        </div>
        <div style={styles.calendarGrid}>{days}</div>
      </div>
    );
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Schedule Deliveries</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={styles.modalBody}>
          <p style={styles.bodyText}>
            You are about to schedule {selectedOrderIds.length} deliver{selectedOrderIds.length !== 1 ? 'ies' : 'y'}.
          </p>

          {selectedDeliveries.length > 0 && (
            <div style={styles.deliveryListContainer}>
              <h4 style={styles.deliveryListHeader}>Selected Deliveries:</h4>
              <ul style={styles.deliveryList}>
                {selectedDeliveries.map(order => (
                  <li key={order.id} style={styles.deliveryListItem}>
                    {order.customerName} - {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={styles.datePickerSection}>
            <label style={styles.datePickerLabel}>Delivery Date:</label>
            {renderCalendar()}
          </div>

          <p style={styles.bodyText}>This will add the selected deliveries to your schedule.</p>
        </div>

        <div style={styles.modalFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button type="button" style={styles.confirmButton} onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalBody: {
    padding: '20px'
  },
  bodyText: {
    margin: '0 0 12px 0',
    fontSize: '15px',
    color: '#555',
    lineHeight: '1.5'
  },
  deliveryListContainer: {
    marginTop: '16px',
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e9ecef'
  },
  deliveryListHeader: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  deliveryList: {
    margin: 0,
    padding: '0 0 0 20px',
    listStyleType: 'disc'
  },
  deliveryListItem: {
    fontSize: '14px',
    color: '#555',
    lineHeight: '1.8',
    marginBottom: '4px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'white',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  confirmButton: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  datePickerSection: {
    marginTop: '20px',
    marginBottom: '20px'
  },
  datePickerLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '12px'
  },
  calendarContainer: {
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    padding: '12px',
    maxWidth: '320px'
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '8px 0'
  },
  calendarNavButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#666',
    cursor: 'pointer',
    padding: '4px 12px',
    fontWeight: 'bold'
  },
  calendarMonthYear: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#333'
  },
  calendarWeekdays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    marginBottom: '8px'
  },
  calendarWeekday: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textAlign: 'center',
    padding: '4px'
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px'
  },
  calendarDay: {
    padding: '8px',
    fontSize: '13px',
    border: '1px solid transparent',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'center',
    color: '#333',
    transition: 'all 0.2s'
  },
  calendarDayEmpty: {
    padding: '8px'
  },
  calendarDaySelected: {
    backgroundColor: '#2196F3',
    color: 'white',
    fontWeight: '600',
    border: '1px solid #2196F3'
  },
  calendarDayToday: {
    border: '1px solid #2196F3',
    fontWeight: '600'
  },
  calendarDayPast: {
    color: '#ccc',
    cursor: 'not-allowed',
    opacity: 0.5
  }
};

export default ScheduleDeliveriesModal;
